// Photos de terrain (D-051) : réduction sur l'appareil à 1 600 px de grand côté en JPEG, empreinte
// SHA-256 du fichier réduit, avant enregistrement en IndexedDB puis envoi une par requête. Le
// redimensionnement passe par un canevas ; il est remplaçable (tests sans canevas).

export const COTE_MAX_PX = 1600;
export const QUALITE_JPEG = 0.82;
export const TYPES_ACCEPTES = ['image/jpeg', 'image/png', 'image/webp'];
/** Taille maximale d'un fichier source accepté (photo de téléphone récente : 3 à 12 Mo). */
export const TAILLE_MAX_SOURCE = 25 * 1024 * 1024;

export interface PhotoReduite {
  fichier: Blob;
  type: 'image/jpeg';
  largeur: number;
  hauteur: number;
  taille: number;
  empreinte: string;
}

export type Redimensionneur = (source: Blob, max: number, qualite: number) => Promise<{ fichier: Blob; largeur: number; hauteur: number }>;

export interface OptionsReduction {
  max?: number;
  qualite?: number;
  redimensionner?: Redimensionneur;
}

export function dimensionsReduites(largeur: number, hauteur: number, max = COTE_MAX_PX): { largeur: number; hauteur: number; reduite: boolean } {
  const plusGrand = Math.max(largeur, hauteur);
  if (plusGrand <= max || plusGrand === 0) return { largeur, hauteur, reduite: false };
  const facteur = max / plusGrand;
  return { largeur: Math.max(1, Math.round(largeur * facteur)), hauteur: Math.max(1, Math.round(hauteur * facteur)), reduite: true };
}

export async function sha256Hex(donnees: Blob | ArrayBuffer | Uint8Array): Promise<string> {
  const tampon = donnees instanceof Blob ? await donnees.arrayBuffer() : donnees instanceof Uint8Array ? donnees : new Uint8Array(donnees);
  const empreinte = await crypto.subtle.digest('SHA-256', tampon instanceof Uint8Array ? (tampon as Uint8Array<ArrayBuffer>) : tampon);
  return Array.from(new Uint8Array(empreinte), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function verifierSource(fichier: Blob): string | null {
  if (fichier.size === 0) return 'Le fichier est vide.';
  if (fichier.size > TAILLE_MAX_SOURCE) return 'La photo dépasse 25 Mo.';
  if (fichier.type && !TYPES_ACCEPTES.includes(fichier.type)) return `Format ${fichier.type} non pris en charge (JPEG, PNG ou WebP attendu).`;
  return null;
}

async function decoder(source: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(source, { imageOrientation: 'from-image' });
  } catch {
    // Option d'orientation inconnue de certains navigateurs : nouvel essai sans elle.
    return createImageBitmap(source);
  }
}

/** Redimensionnement par canevas (OffscreenCanvas si disponible, sinon canevas du document). */
export const redimensionnerParCanevas: Redimensionneur = async (source, max, qualite) => {
  const image = await decoder(source);
  try {
    const d = dimensionsReduites(image.width, image.height, max);
    if (typeof OffscreenCanvas === 'function') {
      const toile = new OffscreenCanvas(d.largeur, d.hauteur);
      const ctx = toile.getContext('2d');
      if (!ctx) throw new Error('Canevas indisponible');
      ctx.drawImage(image, 0, 0, d.largeur, d.hauteur);
      return { fichier: await toile.convertToBlob({ type: 'image/jpeg', quality: qualite }), largeur: d.largeur, hauteur: d.hauteur };
    }
    const toile = document.createElement('canvas');
    toile.width = d.largeur;
    toile.height = d.hauteur;
    const ctx = toile.getContext('2d');
    if (!ctx) throw new Error('Canevas indisponible');
    ctx.drawImage(image, 0, 0, d.largeur, d.hauteur);
    const fichier = await new Promise<Blob>((resoudre, rejeter) => toile.toBlob((b) => (b ? resoudre(b) : rejeter(new Error('Encodage JPEG impossible'))), 'image/jpeg', qualite));
    return { fichier, largeur: d.largeur, hauteur: d.hauteur };
  } finally {
    image.close();
  }
};

/** Réduit une photo (1 600 px, JPEG) et calcule son empreinte SHA-256. */
export async function reduirePhoto(source: Blob, options: OptionsReduction = {}): Promise<PhotoReduite> {
  const refus = verifierSource(source);
  if (refus) throw new Error(refus);
  const redimensionner = options.redimensionner ?? redimensionnerParCanevas;
  const r = await redimensionner(source, options.max ?? COTE_MAX_PX, options.qualite ?? QUALITE_JPEG);
  const empreinte = await sha256Hex(r.fichier);
  return { fichier: r.fichier, type: 'image/jpeg', largeur: r.largeur, hauteur: r.hauteur, taille: r.fichier.size, empreinte };
}

export function nomFichierPhoto(idClient: string): string {
  return `photo-${idClient}.jpg`;
}

export function tailleLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}
